import React from 'react';
import { Prompt, Redirect } from 'react-router-dom';
import Formsy from 'formsy-react';
import { menuAPI } from '../../InterfaceAPI/menuAPI';
import ModalEnabledComponent from '../extend/ModalEnabled';

import MenuWeeklyLayout from '../menu/MenuWeeklyLayout';
import MenuOccasionLayout from '../menu/MenuOccasionLayout';
import MenuEvergreenLayout from '../menu/MenuEvergreenLayout';
import MenuCompanyLayout from '../menu/MenuCompanyLayout';
import MenuArchivedLayout from '../menu/MenuArchivedLayout';

import AddDishModal from '../shared/AddDishModal';
import MenuEditDishModal from '../menu/MenuEditDishModal';
import MenuDeleteModal from '../menu/MenuDeleteModal';
import MenuArchiveModal from '../menu/MenuArchiveModal';
import MenuRestoreModal from '../menu/MenuRestoreModal';
import TextAreaModal from '../shared/TextAreaModal';
import MenuDuplicateModal from '../menu/MenuDuplicateModal';
import MenuSendOccasionModal from '../menu/MenuSendOccasionModal';
import { purgeIDs, stringfyServerErrors } from '../../helpers/util';
import withSplashMessage from '../splashMessage/withSplashMessage';
import withTracker from '../withTracker';
import Loader from '../shared/Loader';

class AdminMenu extends ModalEnabledComponent {
  static stateResetDefaults = {
    unsaved: false,
    isEdit: false,
    courseIndex: -1,
    dishIndex: -1,
    activeDish: {},
    redirect_to: false,
  };

  state = {
    menu: {
      name: '',
      courses: [],
    },
    isLoading: false,
    saveInProgress: false,
    ...AdminMenu.stateResetDefaults,
  };

  render() {
    const { menu, activeDish, unsaved, redirect_to, isLoading } = this.state;
    const {
      addDish,
      updateMenu,
      isOpen,
      closeModal,
      handleDelete,
      handleArchive,
      handleRestore,
      editMenuDish,
    } = this;

    if (redirect_to) return <Redirect to={redirect_to} />;

    if (isLoading) return <Loader />;

    return (
      <div className="menu cms">
        <Formsy className="form">{this.renderMenu()}</Formsy>
        <AddDishModal
          addDish={addDish}
          isOpen={isOpen('dish')}
          closeModal={closeModal}
        />

        <MenuEditDishModal
          editMenuDish={editMenuDish}
          dish={activeDish}
          isOpen={isOpen('edit-dish')}
          closeModal={closeModal}
        />

        <TextAreaModal
          title="Private Notes"
          value={menu.notes}
          isOpen={isOpen('notes')}
          closeModal={closeModal}
          onSubmit={(notes) => updateMenu({ notes })}
        />

        <MenuDeleteModal
          menu={menu}
          deleteMenu={handleDelete}
          isOpen={isOpen('delete')}
          closeModal={closeModal}
        />

        <MenuArchiveModal
          menu={menu}
          archiveMenu={handleArchive}
          isOpen={isOpen('archive')}
          closeModal={closeModal}
        />

        <MenuRestoreModal
          menu={menu}
          restoreMenu={handleRestore}
          isOpen={isOpen('restore')}
          closeModal={closeModal}
        />

        <MenuDuplicateModal
          menu={menu}
          isOpen={isOpen('duplicate')}
          closeModal={closeModal}
        />

        <MenuSendOccasionModal
          menu={menu}
          isOpen={isOpen('send-occasion')}
          closeModal={closeModal}
        />

        <Prompt when={unsaved} message="You have unsaved data, are you sure?" />
      </div>
    );
  }

  renderMenu() {
    const {
      addCourse,
      updateCourse,
      deleteCourse,
      deleteDish,
      toggleEdit,
      updateMenu,
      isOpen,
      closeModal,
      openModal,
      submitSave,
      submitPublish,
      submitSaveDraft,
      editMenuDish,
      props,
    } = this;
    const { menu, isEdit, saveInProgress } = this.state;
    const childProps = {
      ...props,
      menu,
      isEdit,
      saveInProgress,
      toggleEdit,
      updateMenu,
      addCourse,
      updateCourse,
      deleteCourse,
      deleteDish,
      isOpen,
      closeModal,
      openModal,
      submitSave,
      submitPublish,
      submitSaveDraft,
      editMenuDish,
    };

    if (menu.archived === true) {
      return <MenuArchivedLayout {...childProps} />;
    }

    switch (menu.occurrence) {
      case 'weekly':
        return <MenuWeeklyLayout {...childProps} />;
      case 'occasion':
        return <MenuOccasionLayout {...childProps} />;
      case 'company':
        return <MenuCompanyLayout {...childProps} />;
      default:
        return <MenuEvergreenLayout {...childProps} />;
    }
  }

  checkForMissingNameFieldBeforeSubmit = (menu) => {
    if (menu.name === '') {
      // require name before saving or publishing
      this.props.addMessage({
        className: 'error',
        message: 'You must enter a name before saving',
      });
    } else {
      this.handleRequest(menu);
    }
  };

  hasCoursesNames(menu) {
    if (menu.courses.length) {
      for (let i = 0; i < menu.courses.length; i++) {
        if (!menu.courses[i].name) {
          return false;
        }
        return true;
      }
    }
  }

  checkData(menu) {
    if (this.hasCoursesNames(menu)) {
      this.checkForMissingNameFieldBeforeSubmit(menu);
    } else {
      this.props.addMessage({
        className: 'error',
        message: 'Course names are required',
      });
    }
  }

  submitSave = () => {
    const menu = { ...this.state.menu };
    this.checkData(menu);
  };

  submitSaveDraft = () => {
    const menu = { ...this.state.menu };
    menu.status = 'drafted';
    this.checkData(menu);
  };

  submitPublish = () => {
    const menu = { ...this.state.menu };
    menu.status = 'queued';
    this.checkData(menu);
  };

  handleRequest = (menu) => {
    // set saveInProgress flag to disable save button. Avoids double-saving, which was leading to errors.
    this.setState({ saveInProgress: true, isLoading: true });
    if ('id' in menu && menu.id > 0)
      menuAPI
        .updateMenu(menu)
        .then((menu) => {
          this.props.addMessage({
            className: 'success',
            message: `You updated "${menu.name}"`,
          });
          this.setState({
            // redirect_to: `/admin/menus/${menu.occurrence}`,
            saveInProgress: false,
            menu,
            ...AdminMenu.stateResetDefaults,
            isLoading: false,
          });
        })
        .catch((error) =>
          this.props.addMessage({
            className: 'error',
            message: stringfyServerErrors(error),
          })
        );
    else
      menuAPI
        .createMenu(menu)
        .then((menu) => {
          this.props.addMessage({
            className: 'success',
            message: `You created "${menu.name}"`,
          });
          this.setState({
            redirect_to: `/admin/menu/${menu.id}`,
            saveInProgress: false,
            isLoading: false,
          });
        })
        .catch((error) =>
          this.props.addMessage({
            className: 'error',
            message: stringfyServerErrors(error),
          })
        );
  };

  handleDelete = () => {
    const { menu } = this.state;
    menuAPI
      .deleteMenu(menu.id)
      .then((response) => {
        this.props.addMessage({
          className: 'success',
          message: `"${menu.name}" menu has been deleted.`,
        });
        this.setState({ redirect_to: `/admin/menus/${menu.occurrence}` });
      })
      .catch((error) => {
        console.log(error);
        this.props.addMessage({
          className: 'error',
          message: stringfyServerErrors(error),
        });
        this.setState({ openModal: '' });
      });
  };

  handleArchive = () => {
    const { menu } = this.state;
    menuAPI
      .archiveMenu(menu.id)
      .then((response) => {
        this.props.addMessage({
          className: 'success',
          message: `"${menu.name}" menu has been archived.`,
        });
        this.setState({ redirect_to: `/admin/menus/archived` });
      })
      .catch((error) => {
        console.log(error);
        this.props.addMessage({
          className: 'error',
          message: stringfyServerErrors(error),
        });
        this.setState({ openModal: '' });
      });
  };

  handleRestore = () => {
    const { menu } = this.state;
    menuAPI
      .restoreMenu(menu.id)
      .then((response) => {
        this.props.addMessage({
          className: 'success',
          message: `"${menu.name}" menu has been restored.`,
        });
        this.setState({ redirect_to: `/admin/menus/archived` });
      })
      .catch((error) => {
        console.log(error);
        this.props.addMessage({
          className: 'error',
          message: stringfyServerErrors(error),
        });
        this.setState({ openModal: '' });
      });
  };

  addDish = (dish) => {
    const menu = { ...this.state.menu };
    if (!('dishes' in menu.courses[this.state.courseIndex]))
      menu.courses[this.state.courseIndex].dishes = [];
    menu.courses[this.state.courseIndex].dishes.push(dish);
    this.updateMenu(menu);
  };

  deleteDish = (courseIndex, dish) => {
    const menu = { ...this.state.menu };
    const dishIndex = menu.courses[courseIndex].dishes.findIndex(
      (d) => d.id === dish.id
    );
    if (dishIndex > -1)
      menu.courses[courseIndex].dishes = [
        ...menu.courses[courseIndex].dishes.slice(0, dishIndex),
        ...menu.courses[courseIndex].dishes.slice(
          dishIndex + 1,
          menu.courses[courseIndex].dishes.length
        ),
      ];
    this.updateMenu(menu);
  };

  editMenuDish = (dishPart, isCreate = false, isPrivate = false) => {
    // get the full dish to duplicate
    menuAPI
      .getDish(dishPart.id)
      .then((dish) =>
        isCreate
          ? this.createMenuDish(dish, isPrivate)
          : this.redirectToDish(dish)
      )
      .catch((error) => console.log(error));
  };

  createMenuDish = (dish, isPrivate) => {
    dish.parent_dish_id = dish.parent_dish_id ? dish.parent_dish_id : dish.id;
    dish.id = null;
    dish.child_name = dish.child_name ? `${dish.child_name} Copy` : 'Copy';
    dish.components = purgeIDs(dish.components);
    dish.child_private = isPrivate;

    this.redirectToDish(dish);
  };

  redirectToDish = (dish) => {
    const { menu, courseIndex, dishIndex } = this.state;
    this.setState({ unsaved: false });
    const pathname = dish.id ? `/admin/dish/${dish.id}` : '/admin/dish/';
    this.props.history.push({
      pathname,
      state: {
        dish, // loaded by Dish
        redirect_url: this.props.match.url,
        redirect_payload: { menu, courseIndex, dishIndex },
      },
    });
  };

  updateCourse = (i, newCourse) => {
    const menu = { ...this.state.menu };
    menu.courses[i] = { ...menu.courses[i], ...newCourse };
    this.updateMenu(menu);
  };

  addCourse = () => {
    const menu = { ...this.state.menu };
    menu.courses.push({
      name: '',
      description: '',
      dishes: [],
      sort_order: menu.courses.length,
    });
    this.updateMenu(menu);
  };

  deleteCourse = (index) => {
    const menu = { ...this.state.menu };

    // if found, create new courses array without the deleted
    if (index > -1)
      menu.courses = [
        ...menu.courses.slice(0, index),
        ...menu.courses.slice(index + 1, menu.courses.length),
      ];

    this.updateMenu(menu);
  };

  toggleEdit = () => {
    this.setState({ isEdit: !this.state.isEdit });
  };

  generateNew(occurrence) {
    // pre-populate courses for 'weekly' menus
    const menu = {
      occurrence,
      name: '',
      courses:
        occurrence === 'weekly'
          ? [
              {
                name: 'Starters, Salads & Light Veg',
                dishes: [],
                sort_order: 0,
              },
              {
                name: 'Pastas, Grains & Hearty Veg',
                dishes: [],
                sort_order: 1,
              },
              { name: 'Pure Protein', dishes: [], sort_order: 2 },
              { name: 'Sweetness', dishes: [], sort_order: 3 },
            ]
          : [],
    };
    if (occurrence === 'evergreen') menu['status'] = 'drafted';

    this.setState({ menu, isEdit: true });
  }

  updateMenu = (menu, unsaved = true) => {
    const newMenu = { ...this.state.menu, ...menu };
    this.setState({ menu: newMenu, openModal: '', unsaved, isLoading: false });
  };

  loadMenu() {
    const { id, type } = this.props.match.params;
    const { state } = this.props.location;

    this.setState({
      isLoading: true,
    });

    if (state) {
      if (state.dish)
        state.menu.courses[state.courseIndex].dishes[state.dishIndex] =
          state.dish;
      this.setState({
        menu: { ...state.menu },
        unsaved: true,
        isEdit: true,
        isLoading: false,
      });
    } else if (id === 'create') {
      this.generateNew(type);
      this.setState({ isLoading: false });
    } else
      menuAPI
        .getMenu(id)
        .then((menu) => this.setState({ menu, isLoading: false }))
        .catch(this.handleErrorLoading);
  }

  handleErrorLoading = (error) => {
    const pathname = '/admin/menus';
    const redirect_to = { pathname };

    const { state } = this.props.location;
    if (state) {
      const { redirect_url, redirect_payload } = state;
      if (redirect_url) {
        redirect_to.pathname = redirect_url;
      }

      if (redirect_payload) redirect_to.state = { ...redirect_payload };
    }
    this.props.addMessage({
      className: 'error',
      message: stringfyServerErrors(error),
    });
    this.setState({ redirect_to, isLoading: false });
  };

  componentDidMount() {
    this.loadMenu();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.state.redirect_to !== false) this.setState({ redirect_to: false });

    if (prevProps.match.url !== this.props.match.url) this.loadMenu();
  }
}

export default withSplashMessage(withTracker(AdminMenu));



// WEBPACK FOOTER //
// ./src/components/admin/AdminMenu.js